import { Injectable, inject } from '@angular/core';
import { BorrowerLedgerEntry, BorrowerLedgerEntryType, FullLoanDeal, LoanType } from '../models/loan.model';
import { BORROWER_LEDGER_ENTRY_TYPE } from '../constants';
import { CurrencyService } from './currency.service';
import { DayCountService } from './day-count.service';

export interface PaymentAllocation {
  feesPaid: number;
  interestPaid: number;
  principalPaid: number;
  remainingSurplus: number;
}

export type CalculationCutoffPolicy = 'today' | 'loanEnd' | 'redemption';

export interface OutstandingOptions {
  asOfDate?: string;
  cutoffPolicy?: CalculationCutoffPolicy;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentWaterfallService {
  private currencyService = inject(CurrencyService);
  private dayCountService = inject(DayCountService);

  calculateAllocation(
    deal: FullLoanDeal,
    paymentAmount: number,
    asOfDateOrOptions?: string | OutstandingOptions
  ): PaymentAllocation {
    let remainingPayment = paymentAmount;
    const outstanding = this.calculateBorrowerOutstandingAmounts(deal, asOfDateOrOptions);

    const feesPaid = Math.min(remainingPayment, outstanding.fees);
    remainingPayment -= feesPaid;

    const interestPaid = Math.min(remainingPayment, outstanding.interest);
    remainingPayment -= interestPaid;

    const principalPaid = Math.min(remainingPayment, outstanding.principal);
    remainingPayment -= principalPaid;

    const remainingSurplus = remainingPayment;

    return { feesPaid, interestPaid, principalPaid, remainingSurplus };
  }

  calculateBorrowerOutstandingAmounts(
    deal: FullLoanDeal,
    asOfDateOrOptions?: string | OutstandingOptions
  ): { principal: number; interest: number; fees: number; total: number; } {
    if (!deal) return { principal: 0, interest: 0, fees: 0, total: 0 };

    const options = this.resolveOutstandingOptions(asOfDateOrOptions);
    const cutoffDate = this.getCutoffDate(deal, options);
    const ledgerEntries = this.filterLedgerEntriesByDate(deal.borrowerLedger, cutoffDate);

    const totalDrawn = ledgerEntries
      .filter(entry => entry.entryType === BORROWER_LEDGER_ENTRY_TYPE.DRAWDOWN)
      .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);

    const totalPrincipalRepaid = ledgerEntries
      .filter(entry => entry.entryType === BORROWER_LEDGER_ENTRY_TYPE.CAPITAL_REPAYMENT)
      .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);

    const basePrincipal = this.usesDrawdownBasedPrincipal(deal.loanDeal.loanType)
      ? totalDrawn
      : Math.max(0, deal.loanDeal.grossLoanAmount);
    const outstandingPrincipal = Math.max(0, basePrincipal - totalPrincipalRepaid);

    const interestSnapshot = this.getBorrowerInterestSnapshot(deal, options);
    const outstandingInterest = interestSnapshot.outstandingInterest;

    const feesCharged = ledgerEntries
      .filter(entry =>
        entry.entryType === BORROWER_LEDGER_ENTRY_TYPE.FEE_CHARGED ||
        entry.entryType === BORROWER_LEDGER_ENTRY_TYPE.NON_UTILISATION_FEE
      )
      .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);

    const feesPaid = ledgerEntries
      .filter(entry => entry.entryType === BORROWER_LEDGER_ENTRY_TYPE.FEE_PAYMENT)
      .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);

    const outstandingFees = Math.max(0, feesCharged - feesPaid);

    const total = outstandingPrincipal + outstandingInterest + outstandingFees;

    return {
      principal: outstandingPrincipal,
      interest: outstandingInterest,
      fees: outstandingFees,
      total,
    };
  }

  getBorrowerInterestSnapshot(
    deal: FullLoanDeal,
    asOfDateOrOptions?: string | OutstandingOptions
  ): { totalInterestAccrued: number; totalInterestPaid: number; outstandingInterest: number } {
    const options = this.resolveOutstandingOptions(asOfDateOrOptions);
    const cutoffDate = this.getCutoffDate(deal, options);
    const { totalInterestAccrued } = this.calculateBorrowerInterest(deal, cutoffDate);
    const ledgerEntries = this.filterLedgerEntriesByDate(deal.borrowerLedger, cutoffDate);

    const totalInterestPaid = ledgerEntries
      .filter(entry => entry.entryType === BORROWER_LEDGER_ENTRY_TYPE.INTEREST_PAYMENT)
      .reduce((sum, entry) => sum + Math.abs(entry.amount), 0);

    const outstandingInterest = Math.max(0, totalInterestAccrued - totalInterestPaid);

    return {
      totalInterestAccrued,
      totalInterestPaid,
      outstandingInterest,
    };
  }

  private calculateBorrowerInterest(deal: FullLoanDeal, cutoffDate: Date): { totalInterestAccrued: number } {
    const startDate = this.getInterestStartDate(deal);
    if (!startDate) {
      return { totalInterestAccrued: 0 };
    }

    const endDate = cutoffDate >= startDate ? cutoffDate : startDate;
    const dayCountConvention = deal.financialTerms.dayCountConvention;
    const daysInYear = this.dayCountService.getDaysInYear(dayCountConvention);

    const drawdownBasedPrincipal = this.usesDrawdownBasedPrincipal(deal.loanDeal.loanType);
    const drawdownPaymentsByDate = drawdownBasedPrincipal
      ? this.mapLedgerEntriesByDate(
        deal.borrowerLedger,
        BORROWER_LEDGER_ENTRY_TYPE.DRAWDOWN,
        cutoffDate,
        false
      )
      : new Map<string, number>();

    const capitalRepaymentsByDate = this.mapLedgerEntriesByDate(
      deal.borrowerLedger,
      BORROWER_LEDGER_ENTRY_TYPE.CAPITAL_REPAYMENT,
      cutoffDate,
      true
    );

    const events = [...(deal.events ?? [])].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    let totalInterestAccruedPenceExact = 0;
    let principalPence = drawdownBasedPrincipal ? 0 : this.currencyService.toPence(deal.loanDeal.grossLoanAmount);
    let eventIndex = 0;
    let currentRate = deal.financialTerms.borrowerInterestRate;

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = this.toDateKey(d);
      const dailyAccrualDays = this.dayCountService.getAccrualDaysForDate(d, dayCountConvention);

      if (drawdownBasedPrincipal && drawdownPaymentsByDate.has(dateKey)) {
        principalPence += this.currencyService.toPence(drawdownPaymentsByDate.get(dateKey)!);
      }

      if (capitalRepaymentsByDate.has(dateKey)) {
        principalPence -= this.currencyService.toPence(capitalRepaymentsByDate.get(dateKey)!);
        principalPence = Math.max(0, principalPence);
      }

      while (eventIndex < events.length && new Date(events[eventIndex].date) <= d) {
        const event = events[eventIndex];
        if (event.newBorrowerRate != null) {
          currentRate = event.newBorrowerRate;
        } else if (event.type === 'Default') {
          currentRate = deal.financialTerms.borrowerDefaultInterestRate;
        }
        eventIndex++;
      }

      if (principalPence > 0) {
        const dailyInterestPence = this.currencyService.calculateInterestExactPence(
          principalPence,
          currentRate,
          dailyAccrualDays,
          daysInYear
        );
        totalInterestAccruedPenceExact += Math.max(0, dailyInterestPence);
      }
    }

    return {
      totalInterestAccrued: this.currencyService.fromPence(
        this.currencyService.roundPence(totalInterestAccruedPenceExact)
      )
    };
  }

  private filterLedgerEntriesByDate(
    entries: BorrowerLedgerEntry[] | undefined,
    cutoffDate: Date
  ): BorrowerLedgerEntry[] {
    return (entries ?? []).filter(entry => {
      const entryDate = this.getEntryDate(entry);
      return entryDate <= cutoffDate;
    });
  }

  private mapLedgerEntriesByDate(
    entries: BorrowerLedgerEntry[] | undefined,
    type: BorrowerLedgerEntryType,
    cutoffDate: Date,
    useAbsoluteAmount: boolean
  ): Map<string, number> {
    const map = new Map<string, number>();
    for (const entry of entries ?? []) {
      if (entry.entryType !== type) continue;
      const entryDate = this.getEntryDate(entry);
      if (entryDate > cutoffDate) continue;
      const amount = useAbsoluteAmount ? Math.abs(entry.amount) : entry.amount;
      const dateKey = this.toDateKey(entryDate);
      map.set(dateKey, (map.get(dateKey) || 0) + amount);
    }
    return map;
  }

  private getEntryDate(entry: BorrowerLedgerEntry): Date {
    return entry.valueDate ? new Date(entry.valueDate) : new Date(entry.date);
  }

  private toDateKey(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private toDateEndOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  private parseAsOfDate(input?: string): Date | null {
    if (!input) return null;

    const dateOnlyMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59, 999);
    }

    const parsed = new Date(input);
    if (isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  }

  private resolveOutstandingOptions(asOfDateOrOptions?: string | OutstandingOptions): Required<OutstandingOptions> {
    if (typeof asOfDateOrOptions === 'string') {
      return {
        asOfDate: asOfDateOrOptions,
        cutoffPolicy: 'today',
      };
    }

    return {
      asOfDate: asOfDateOrOptions?.asOfDate || '',
      cutoffPolicy: asOfDateOrOptions?.cutoffPolicy || 'today',
    };
  }

  private getCutoffDate(deal: FullLoanDeal, options: Required<OutstandingOptions>): Date {
    if (options.asOfDate) {
      const parsed = this.parseAsOfDate(options.asOfDate);
      if (parsed) {
        return parsed;
      }
    }

    if (options.cutoffPolicy === 'loanEnd' && deal.loanDeal.loanEndDate) {
      const loanEndDate = this.parseAsOfDate(deal.loanDeal.loanEndDate);
      if (loanEndDate) {
        return loanEndDate;
      }
    }

    if (options.cutoffPolicy === 'redemption' && deal.loanDeal.actualRedemptionDate) {
      const redemptionDate = this.parseAsOfDate(deal.loanDeal.actualRedemptionDate);
      if (redemptionDate) {
        return redemptionDate;
      }
    }

    return this.toDateEndOfDay(new Date());
  }

  private getInterestStartDate(deal: FullLoanDeal): Date | null {
    // Priority 1: Use actual disbursement date if available (when funds were actually disbursed)
    if (deal.loanDeal.actualDisbursementDate) {
      return new Date(deal.loanDeal.actualDisbursementDate);
    }
    // Priority 2: Fall back to loan start date (contractual start date)
    if (deal.loanDeal.loanStartDate) {
      return new Date(deal.loanDeal.loanStartDate);
    }
    // Priority 3: Use earliest borrower ledger entry date as last resort
    const ledgerDates = (deal.borrowerLedger ?? [])
      .map(entry => this.getEntryDate(entry))
      .sort((a, b) => a.getTime() - b.getTime());
    return ledgerDates[0] ?? null;
  }

  /**
   * Calculate the interest spread between borrower payment and investor portion.
   * This shows how much of the borrower's interest payment goes to investors vs. broker/platform.
   * 
   * PERIODIC FEE LOGIC:
   * - Borrower pays at borrowerInterestRate (e.g., 12.75%)
   * - Investors receive at clientInterestRate (e.g., 10%)
   * - Difference is split between Broker and Arris (platform)
   * - Broker portion is tracked for periodic invoicing
   * 
   * @param principalAmount - The principal balance
   * @param borrowerRate - The rate charged to the borrower
   * @param investorRate - The rate paid to investors
   * @param brokerRate - The broker's fee rate
   * @param arrisRate - The platform's fee rate  
   * @param days - Number of days for the calculation
   * @param daysInYear - Day count convention (365, 360, etc.)
   * @returns Breakdown of interest payments
   */
  calculateInterestSplit(
    principalAmount: number,
    borrowerRate: number,
    investorRate: number,
    brokerRate: number,
    arrisRate: number,
    days: number,
    daysInYear: number = 365
  ): {
    totalBorrowerPayment: number;
    investorShare: number;
    brokerShare: number;
    arrisShare: number;
    totalSpread: number;
  } {
    const principalPence = this.currencyService.toPence(principalAmount);

    // Calculate each component in pence for precision
    const totalBorrowerPaymentPence = this.currencyService.calculateInterest(
      principalPence,
      borrowerRate,
      days,
      daysInYear
    );

    const investorSharePence = this.currencyService.calculateInterest(
      principalPence,
      investorRate,
      days,
      daysInYear
    );

    const brokerSharePence = this.currencyService.calculateInterest(
      principalPence,
      brokerRate,
      days,
      daysInYear
    );

    const arrisSharePence = this.currencyService.calculateInterest(
      principalPence,
      arrisRate,
      days,
      daysInYear
    );

    // Convert back to pounds
    const totalBorrowerPayment = this.currencyService.fromPence(totalBorrowerPaymentPence);
    const investorShare = this.currencyService.fromPence(investorSharePence);
    const brokerShare = this.currencyService.fromPence(brokerSharePence);
    const arrisShare = this.currencyService.fromPence(arrisSharePence);

    // The spread is broker + arris (everything not going to investors)
    const totalSpread = brokerShare + arrisShare;

    return {
      totalBorrowerPayment,
      investorShare,
      brokerShare,
      arrisShare,
      totalSpread
    };
  }

  /**
   * Calculate accrued broker fees for periodic invoicing.
   * This represents the broker's share of interest that has accrued but not yet been invoiced.
   * 
   * @param deal - Full loan deal
   * @param asOfDate - Calculate fees up to this date (defaults to today)
   * @returns Total broker fees accrued for invoicing
   */
  getAccruedBrokerFees(deal: FullLoanDeal, asOfDateOrOptions?: string | OutstandingOptions): number {
    const options = this.resolveOutstandingOptions(asOfDateOrOptions);
    const cutoffDate = this.getCutoffDate(deal, options);
    const startDate = this.getInterestStartDate(deal);

    if (!startDate) {
      return 0;
    }

    const endDate = cutoffDate >= startDate ? cutoffDate : startDate;
    const dayCountConvention = deal.financialTerms.dayCountConvention;
    const daysInYear = this.dayCountService.getDaysInYear(dayCountConvention);
    const brokerRate = deal.financialTerms.brokerInterestRate || 0;

    // Get principal balance history
    const drawdownBasedPrincipal = this.usesDrawdownBasedPrincipal(deal.loanDeal.loanType);
    const drawdownPaymentsByDate = drawdownBasedPrincipal
      ? this.mapLedgerEntriesByDate(
        deal.borrowerLedger,
        BORROWER_LEDGER_ENTRY_TYPE.DRAWDOWN,
        cutoffDate,
        false
      )
      : new Map<string, number>();

    const capitalRepaymentsByDate = this.mapLedgerEntriesByDate(
      deal.borrowerLedger,
      BORROWER_LEDGER_ENTRY_TYPE.CAPITAL_REPAYMENT,
      cutoffDate,
      true
    );

    let totalBrokerFeesPenceExact = 0;
    let principalPence = drawdownBasedPrincipal ? 0 : this.currencyService.toPence(deal.loanDeal.grossLoanAmount);

    // Calculate broker fees daily based on outstanding principal
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = this.toDateKey(d);
      const dailyAccrualDays = this.dayCountService.getAccrualDaysForDate(d, dayCountConvention);

      // Update principal balance
      if (drawdownBasedPrincipal && drawdownPaymentsByDate.has(dateKey)) {
        principalPence += this.currencyService.toPence(drawdownPaymentsByDate.get(dateKey)!);
      }

      if (capitalRepaymentsByDate.has(dateKey)) {
        principalPence -= this.currencyService.toPence(capitalRepaymentsByDate.get(dateKey)!);
        principalPence = Math.max(0, principalPence);
      }

      // Accrue broker fees if there's outstanding principal
      if (principalPence > 0 && brokerRate > 0) {
        const dailyBrokerFeePence = this.currencyService.calculateInterestExactPence(
          principalPence,
          brokerRate,
          dailyAccrualDays,
          daysInYear
        );
        totalBrokerFeesPenceExact += Math.max(0, dailyBrokerFeePence);
      }
    }

    return this.currencyService.fromPence(
      this.currencyService.roundPence(totalBrokerFeesPenceExact)
    );
  }

  private usesDrawdownBasedPrincipal(loanType?: LoanType): boolean {
    return loanType === 'Development (Tranches)';
  }
}
